import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { At, ArrowRight, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { mAuth } from "@/lib/api";
import { MERCHANT } from "@/lib/routes";
import { BlonixWordmark } from "@/components/BlonixLogo";
import { Input } from "./MerchantLoginFields";
import { getEmailError, normalizeEmail } from "@/lib/validation";

const RESEND_SEC = 60;

export default function MerchantLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [devOtpHint, setDevOtpHint] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const sendOtp = useCallback(async () => {
    const normalized = normalizeEmail(email);
    const err = getEmailError(email);
    if (err) {
      setEmailError(err);
      setEmailTouched(true);
      toast.error(err);
      return;
    }

    setLoading(true);
    try {
      const data = await mAuth.sendLoginOtp(normalized);
      if (data?.dev_otp) {
        setDevOtpHint(data.dev_otp);
        setOtp(data.dev_otp);
        toast.message("Local dev OTP (email not configured)", {
          description: `Your code is ${data.dev_otp}. It is also printed in the backend terminal.`,
          duration: 12000,
        });
      } else {
        setDevOtpHint("");
        toast.success(data?.message || "OTP sent — check your email");
      }
      setStep("otp");
      if (!data?.dev_otp) setOtp("");
      setResendIn(RESEND_SEC);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send OTP");
    } finally {
      setLoading(false);
    }
  }, [email]);

  const onEmailSubmit = async (e) => {
    e.preventDefault();
    await sendOtp();
  };

  const onEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(getEmailError(email) || "");
  };

  const onEmailChange = (v) => {
    setEmail(v);
    if (emailError) setEmailError("");
  };

  const emailValid = !getEmailError(email);

  const onOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await mAuth.verifyLoginOtp(normalizeEmail(email), otp);
      toast.success("Welcome");
      navigate(MERCHANT.home);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    if (resendIn > 0 || loading) return;
    await sendOtp();
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="merchant-login-page">
      <div className="hidden lg:flex flex-1 bg-zinc-950 text-white p-14 flex-col justify-between">
        <div>
          <BlonixWordmark className="h-6 w-auto" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">Merchant Portal</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mb-3">Sandbox-first by design</div>
          <div className="font-display text-3xl leading-tight tracking-tight max-w-md">
            Issue cards. Move money. Test every edge case before you ship.
          </div>
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-sm text-white/80">
            <Stat n="Sandbox" l="default mode" />
            <Stat n="HMAC" l="optional signing" />
            <Stat n="<5min" l="time to first card" />
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col p-8 lg:p-14 max-w-xl mx-auto w-full">
        <div className="lg:hidden mb-8">
          <BlonixWordmark className="h-6 w-auto" />
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">Merchant Portal</div>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Sign in</div>
            <h1 className="font-display text-4xl tracking-tight font-medium text-zinc-950">Merchant Portal</h1>
            <p className="mt-3 text-sm text-zinc-500">
              {step === "email"
                ? "Enter your work email and we will send a one-time OTP."
                : `Enter the 6-digit OTP we sent to ${email}.`}
            </p>

            {step === "email" ? (
              <form onSubmit={onEmailSubmit} className="mt-10 space-y-5" data-testid="m-login-form" noValidate>
                <Input
                  label="Email"
                  testId="m-login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={onEmailChange}
                  onBlur={onEmailBlur}
                  error={emailTouched ? emailError : ""}
                  icon={At}
                />
                <button
                  type="submit"
                  disabled={loading || !emailValid}
                  data-testid="m-login-submit-btn"
                  className="w-full h-10 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50"
                >
                  {loading ? "Sending OTP…" : (<>Send OTP <ArrowRight size={14} /></>)}
                </button>
                <div className="text-xs text-zinc-500 text-center pt-2">
                  New here?{" "}
                  <Link to={MERCHANT.signup} data-testid="goto-signup" className="text-zinc-950 underline hover:no-underline">Create a merchant account</Link>
                </div>
              </form>
            ) : (
              <form onSubmit={onOtpSubmit} className="mt-10 space-y-5" data-testid="m-login-otp-form">
                {devOtpHint && (
                  <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950" data-testid="dev-otp-hint">
                    Email is not configured on the API server. Your OTP:{" "}
                    <span className="font-mono font-semibold">{devOtpHint}</span>
                  </div>
                )}
                <Input
                  label="OTP"
                  testId="m-login-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  inputClassName="tracking-[0.35em] text-center font-mono"
                  value={otp}
                  onChange={(v) => setOtp(v.replace(/\D/g, "").slice(0, 6))}
                  icon={EnvelopeSimple}
                />
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  data-testid="m-login-verify-btn"
                  className="w-full h-10 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-sm hover:bg-zinc-800 transition-colors duration-150 disabled:opacity-50"
                >
                  {loading ? "Verifying…" : (<>Sign in <ArrowRight size={14} /></>)}
                </button>
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-zinc-700 hover:text-zinc-950"
                    onClick={() => {
                      setStep("email");
                      setOtp("");
                      setDevOtpHint("");
                      setEmailTouched(false);
                      setEmailError("");
                    }}
                    data-testid="m-login-back"
                  >
                    <ArrowLeft size={14} /> Change email
                  </button>
                  <button
                    type="button"
                    disabled={resendIn > 0 || loading}
                    onClick={onResend}
                    data-testid="m-login-resend"
                    className="text-zinc-950 underline hover:no-underline disabled:no-underline disabled:text-zinc-400"
                  >
                    {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 mt-8">
          © {new Date().getFullYear()} Blonix Technologies Limited
        </div>
      </div>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-xl font-medium tabular-nums">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.1em] text-white/60 mt-0.5">{l}</div>
    </div>
  );
}
